import { produce } from 'immer';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../common/widgets/Dialog';
import Icons from '../../../../../../../common/widgets/Icons';
import Input from '../../../../../../../common/widgets/Input';
import RadioGroup from '../../../../../../../common/widgets/RadioGroup';
import TextArea from '../../../../../../../common/widgets/TextArea';
import { ErrorState } from '../../../../../../benefit/models/Errors';
import { PercentageVoucherCodeTypes } from '../../../../../enums';
import { resources } from '../../../../../resources/resources';
import { ProductPreview } from '../../../Common/ProductPreview';
import { ProductPreviewData } from '../../types';

export interface PercentageVoucherOfferDetailsForm {
  customVoucherDiscount?: string;
  voucherDescription?: string;
  randomCode?: boolean;
  codeType?: PercentageVoucherCodeTypes;
  barcode?: string;
}

interface PercentageVoucherOfferDetailsProps {
  previewData?: ProductPreviewData;
  offerDetailsForm: PercentageVoucherOfferDetailsForm;
  isSaveButtonLoading?: boolean;
  errors: ErrorState;
  setErrors: (errors: ErrorState) => void;
  setOfferDetailsForm: (form: PercentageVoucherOfferDetailsForm) => void;
  buttonFunctions?: {
    onBackClick: () => void;
    onContinueClick: () => void;
  };
}

export const PercentageVoucherOfferDetails = (
  props: PercentageVoucherOfferDetailsProps,
) => {
  const {
    previewData,
    isSaveButtonLoading,
    offerDetailsForm,
    setOfferDetailsForm,
    errors,
    setErrors,
    buttonFunctions,
  } = props;
  const [showManualCodeDialog, setShowManualCodeDialog] =
    useState<boolean>(false);
  const { t: translate } = useTranslation();

  const clearErrors = (field: string) => {
    setErrors(
      produce(errors, d => {
        d[field] = [];
      }),
    );
  };

  const saveBarcode = (barcode: string) => {
    setOfferDetailsForm(
      produce(offerDetailsForm, d => {
        d.barcode = barcode;
      }),
    );
    setShowManualCodeDialog(false);
  };

  const closeDialog = () => {
    setOfferDetailsForm(
      produce(offerDetailsForm, d => {
        d.randomCode = true;
        d.codeType = PercentageVoucherCodeTypes.RandomGenerated;
      }),
    );
    setShowManualCodeDialog(false);
  };

  return (
    <>
      {previewData && (
        <ProductPreview
          productTypes={previewData.productTypes}
          producyType={previewData.producyType}
          productStatuses={previewData.productStatuses}
          status={previewData.status}
          name={previewData.name}
          imageContent={previewData.imageContent}
          showPricePreview={false}
        />
      )}
      <div className="m-panel">
        <h1>{translate(resources.products.add.offerDetails.title)}</h1>
        <div className="m-form">
          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.percentageVoucher
                    .customVoucherDiscount.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Input
                value={offerDetailsForm.customVoucherDiscount ?? ''}
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.customVoucherDiscount = e.value
                        ? _.parseInt(e.value).toString()
                        : '';
                    }),
                  )
                }
                type="number"
                mandatory
                errors={errors.customVoucherDiscount}
                clearErrors={() => clearErrors('customVoucherDiscount')}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.percentageVoucher
                    .customVoucherDiscount.info,
                )}
              </span>
            </div>
          </div>

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.percentageVoucher
                    .voucherDescription.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <TextArea
                value={offerDetailsForm.voucherDescription ?? ''}
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.voucherDescription = e.value;
                    }),
                  )
                }
                mandatory
                errors={errors.voucherDescription}
                clearErrors={() => clearErrors('voucherDescription')}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.percentageVoucher
                    .voucherDescription.info,
                )}
              </span>
            </div>
          </div>

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.percentageVoucher.code
                    .label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <RadioGroup
                layout="horizontal"
                errors={errors.codeType}
                clearErrors={() => clearErrors('codeType')}
                onChange={e => {
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.randomCode =
                        e.value === PercentageVoucherCodeTypes.RandomGenerated;
                      d.codeType = e.value;
                      if (
                        e.value === PercentageVoucherCodeTypes.RandomGenerated
                      ) {
                        d.barcode = undefined;
                      }
                    }),
                  );
                  if (e.value === PercentageVoucherCodeTypes.ManuallyAdded) {
                    setShowManualCodeDialog(true);
                  }
                }}
                value={offerDetailsForm.codeType}
                data={[
                  {
                    label: translate(
                      resources.products.add.offerDetails.percentageVoucher.code
                        .generateRandom,
                    ),
                    value: PercentageVoucherCodeTypes.RandomGenerated,
                  },
                  {
                    label: translate(
                      resources.products.add.offerDetails.percentageVoucher.code
                        .addManually.label,
                    ),
                    value: PercentageVoucherCodeTypes.ManuallyAdded,
                  },
                ]}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.percentageVoucher.code
                    .info,
                )}
              </span>
            </div>
          </div>

          {offerDetailsForm.randomCode === false &&
            offerDetailsForm.barcode && (
              <div className="m-form-section">
                <div className="m-form-label">
                  <h4>
                    {translate(
                      resources.products.add.offerDetails.percentageVoucher.code
                        .manuallyAdded,
                    )}
                  </h4>
                </div>
                <div className="m-form-input">
                  <Input
                    value={offerDetailsForm.barcode ?? ''}
                    readOnly
                    controlClassName="mb-0"
                    errors={errors.barcode}
                    clearErrors={() => clearErrors('barcode')}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
      {buttonFunctions && (
        <div className="m-actions-panel">
          <Button
            onClick={buttonFunctions.onBackClick}
            className="btn btn-outline"
            disabled={isSaveButtonLoading}
          >
            <span className="btn-text">{translate(resources.back)}</span>
          </Button>
          <Button
            onClick={buttonFunctions.onContinueClick}
            className="btn btn-solid"
            loading={isSaveButtonLoading}
          >
            <span className="btn-text">
              {translate(resources.saveAndContinue)}
            </span>
          </Button>
        </div>
      )}
      {showManualCodeDialog && (
        <ManualCodeDialog saveBarcode={saveBarcode} closeDialog={closeDialog} />
      )}
    </>
  );
};

interface ManualCodeDialogProps {
  saveBarcode: (barcode: string) => void;
  closeDialog: () => void;
}

const ManualCodeDialog = (props: ManualCodeDialogProps) => {
  const { saveBarcode, closeDialog } = props;
  const [barcode, setBarcode] = useState<string>('');
  const { t: translate } = useTranslation();
  return (
    <Dialog
      showCloseIcon
      closeDialog={closeDialog}
      className="dialog-merchants-edit"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(
            resources.products.add.offerDetails.percentageVoucher.code
              .addManually.dialog.title,
          )}
        </div>
        <div className="dialog-text centered mb-20">
          {translate(
            resources.products.add.offerDetails.percentageVoucher.code
              .addManually.dialog.info,
          )}
        </div>
        <div>
          <Input
            label={translate(
              resources.products.add.offerDetails.percentageVoucher.code
                .addManually.dialog.code.label,
            )}
            value={barcode}
            onChange={e => setBarcode(e.value)}
            mandatory
            placeholder={translate(
              resources.products.add.offerDetails.percentageVoucher.code
                .addManually.dialog.code.placeholder,
            )}
            helper={translate(
              resources.products.add.offerDetails.percentageVoucher.code
                .addManually.dialog.code.info,
            )}
            controlClassName="mb-0"
          />
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button
          onClick={() => saveBarcode(barcode)}
          icon={<Icons.MSaveCode />}
          className="btn btn-solid"
          disabled={!barcode}
        >
          <span className="btn-text">
            {translate(
              resources.products.add.offerDetails.percentageVoucher.code
                .addManually.dialog.save,
            )}
          </span>
        </Button>
      </div>
    </Dialog>
  );
};
